export interface AbbreviationMap {
  [abbreviation: string]: string;
}

export interface AbbreviationTooltip {
  id: string;
  content: string;
  escape: boolean;
}

export interface ProcessedAbbreviationResult {
  content: string;
  tooltips: Array<AbbreviationTooltip>;
}

export class AbbreviationsProcessor {
  private uniqueId: string;
  private processedContent: string;
  private tooltips: Array<AbbreviationTooltip>;
  private abbreviations: AbbreviationMap;

  constructor(content: string) {
    this.uniqueId = `section-${Math.random().toString(36).substring(2, 11)}`;
    this.processedContent = content;
    this.tooltips = [];
    this.abbreviations = {};
  }

  processAbbreviations(
    abbreviations: AbbreviationMap,
    cssClass: string,
    escapeHtml: boolean,
    matchWholeWords: boolean = true
  ): void {
    // Merge abbreviations into internal state
    this.abbreviations = {...this.abbreviations, ...abbreviations};

    let matchIndex = 0;

    Object.entries(abbreviations)
      .sort(([a], [b]) => b.length - a.length)
      .forEach(([abbreviation, fullText]) => {

        const regex = this.buildRegex(abbreviation, matchWholeWords);

        this.processedContent = this.processedContent.replace(regex, (match, opaque, upper, tag, abbr) => {
          // 1. Found an already wrapped abbreviation? -> Skip whole block
          if (opaque) return opaque;

          // 2. Found an uppercase line? -> Skip
          if (upper) return upper;

          // 3. Found a normal tag (p, em, u, div)? -> Skip just the tag
          if (tag) return tag;

          // 4. Found an Abbreviation? -> REPLACE
          if (abbr) {
            const tooltipId = `${this.uniqueId}-${cssClass}-${matchIndex}`;
            matchIndex++;

            return this.wrapAbbreviation(escapeHtml, fullText, tooltipId, cssClass, abbreviation);
          }

          return match;
        });
      });
  }

  private sanitizeAbbreviation(abbreviation: string): string {
    return abbreviation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private flattenParagraphs(html: string): string {
    return html
      // Step 1: Replace the "Gap" (closing p + whitespace + opening p) with <br>
      .replace(/<\/p>\s*<p[^>]*>/g, '<br>')

      // Step 2: Strip the remaining opening <p...> and closing </p> tags
      .replace(/<\/?p[^>]*>/g, '');
  }

  private wrapAbbreviation(escapeHtml: boolean, fullText: string, tooltipId: string, cssClass: string, abbreviation: string) {
    const formattedText = this.flattenParagraphs(fullText);

    this.tooltips.push({
      id: tooltipId,
      content: formattedText,
      escape: escapeHtml
    });

    return `<a id="${tooltipId}" class="${cssClass}">${abbreviation}</a>`;
  }

  private buildRegex(abbreviation: string, matchWholeWords: boolean) {
    const abbrPattern = this.sanitizeAbbreviation(abbreviation);

    // A. OPAQUE WRAPPER (Priority 1: Skip everything)
    // Matches specific tags AND their content (e.g., <abbr>...</abbr>).
    // Usage: Prevents double-substitution or changing text inside specific blocks.
    const opaqueWrapper = String.raw`<a\b[^>]*>[\s\S]*?<\/span>`;

    // B. UPPERCASE LINE (Priority 2: Skip everything)
    // Matches lines containing only Unicode Uppercase letters or spaces.
    const upperCaseLine = String.raw`^[\p{Lu}\s-]+$`;

    // C. HTML ATTRIBUTES/TAGS (Priority 3: Skip the tag, expose the content)
    // Matches any <tag> or </tag>.
    // Usage: Prevents matching text inside class="ABC" or href="ABC".
    const htmlStructure = String.raw`<[^>]+>`;

    // D. TARGETS (Priority 4: The Match)
    // Matches your abbreviations. Ensure 'abbrPattern' is sorted by length (desc).
    const targets = matchWholeWords ?
      String.raw`\b(${abbrPattern})\b` :
      String.raw`(${abbrPattern})`;

    const regex = new RegExp(
      `(${opaqueWrapper})|(${upperCaseLine})|(${htmlStructure})|(${targets})`,
      'gmu'
    );
    return regex;
  }

  resolveTooltips(): void {
    if (Object.keys(this.abbreviations).length === 0) {
      return;
    }

    // Build regex from all abbreviations (sorted by length descending)
    const sortedAbbreviations = Object.keys(this.abbreviations)
      .sort((a, b) => b.length - a.length);

    const pattern = sortedAbbreviations
      .map(abbr => this.sanitizeAbbreviation(abbr))
      .join('|');

    const regex = new RegExp(`\\b(${pattern})\\b`, 'g');

    // Resolve abbreviations in each tooltip's content
    this.tooltips.forEach(tooltip => {
      tooltip.content = tooltip.content.replace(regex, (match) => {
        return this.abbreviations[match] || match;
      });
      // Format paragraphs after resolving abbreviations
      tooltip.content = this.flattenParagraphs(tooltip.content);
    });
  }

  getResult(): ProcessedAbbreviationResult {
    return {
      content: this.processedContent,
      tooltips: this.tooltips
    };
  }
}

